import React, {
    useState, useEffect, useRef, useLayoutEffect, useCallback
} from 'react';
import { injectIntl, intlShape } from 'react-intl';
import {
    Tab, Tabs, TabScrollButton, Typography, Grid, Box, useTheme, useMediaQuery
} from '@mui/material';
import PropTypes from 'prop-types';
import { List as IList } from 'immutable';

import { connect } from 'react-redux';
import windowSize from '../../utils/windowSize';
import FaceplateList from '../Carousel/FaceplateList';
import FaceplateCarousel from '../Carousel/FaceplateCarousel';
import homePageV2Messages from '../assets/messages/homePageMessages';
import SkeletonLoader from '../../loader/SkeletonLoader';
import CarousalCategoryPicker from '../../giftCardDetailsPage_v2/CarousalCategoryPicker';
import { CARD_TYPES } from '../../constants/constants';
import { HOME_STEP } from '../../routing/flowConstants';
import UYOButton from '../../common/UYOButton';
import PlayAllAnimationsButton from '../../common/PlayAllAnimationsButton';
import { PHYSICAL_CARD_TYPE } from '../../productCartHandler/productCartModule';
import {
    saveSelectedCardOnBuyButtonClick,
    selectedCardType, setClickedOnUYO
} from '../../landingPage/landingPageModule';
import { trackButtonClick } from '../../segment/segmentModule';
import { logWarning } from '../../utils/errorUtils';


function AccessibleTabScrollButton(props) {
    const { direction } = props;
    return (
        <TabScrollButton
            {...props}
            component='button'
            type='button'
            aria-label={direction === 'left' ? 'Go to previous design category' : 'Go to next design category'}
        />
    );
}

AccessibleTabScrollButton.propTypes = {
    direction: PropTypes.oneOf(['left', 'right']).isRequired
};


const WRAP_THRESHOLD_PX = 4;

export function CategoryTabsCarouselContainer({
    handleSubmit,
    categories,
    giftCardDeliveryType,
    categoryCount,
    setFaceplatesCount,
    faceplatesCount,
    intl,
    isLoading,
    callbackOnCategorySelectOrClose,
    tempCurrency,
    shouldSetCurrency,
    cardType,
    renderCatalogAndDenom,
    isPhotoSupported,
    isPhysicalCard,
    uyoFaceplate,
    defaultCategory,
    defaultFaceplate,
    saveSelectedCardOnBuyButtonClickAction,
    saveSelectedCardType,
    setClickedOnUYOAction,
    trackAddAnotherButton,
    isNewLandingPage,
    hidePicker,
    checkoutSubHeaderText
}) {
    const faceplateCategory = categories && categories.toJS()[0];
    const [selectedCategory, setSelectedCategory] = useState(faceplateCategory);
    const [selectedCategoryTabIndex, setSelectedCategoryTabIndex] = useState(0);

    const [carouselKey, setCarouselKey] = useState(0);
    const [carouselLoaded, setCarouselLoaded] = useState(false);


    const hasCategories = (categories.size >= 1);

    
    const handleTabChange = (event, newValue) => {
        const changedCategory = categories.toJS()[newValue];
        setSelectedCategory(changedCategory);
        setSelectedCategoryTabIndex(newValue);
        setCarouselLoaded(false);
        trackAddAnotherButton(changedCategory, HOME_STEP, 'Category Pill Click');
    };

    const refreshCarousel = () => setCarouselKey(prevKey => prevKey + 1);

    const handleScrollButtonClick = (e) => {
        try {
            const scrollButton = e.target.closest('.MuiTabScrollButton-root');
            if (!scrollButton) return;
            const container = e.currentTarget;
            if (!container) return;
            const allButtons = container.querySelectorAll('.MuiTabScrollButton-root');
            if (!allButtons || allButtons.length === 0) return;
            const dir = scrollButton === allButtons[0] ? 'Left' : 'Right';
            const cardTypeLabel = isPhysicalCard ? CARD_TYPES.Physical : CARD_TYPES.Digital;
            trackAddAnotherButton(
                `${cardTypeLabel} Category Tab ${dir} Arrow Click`,
                HOME_STEP,
                'Homepage Category Tab Arrow Click'
            );
        } catch (err) {
            logWarning('Category tab arrow tracking failed', { error: err.message });
        }
    };

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

    
    const outerContainerRef = useRef(null);
    const wrapperRef = useRef(null);
    const headerSectionRef = useRef(null);
    const tabsSectionRef = useRef(null);

    const recalcTabsWidth = useCallback(() => {
        const wrapperEl = wrapperRef.current;
        const headerEl = headerSectionRef.current;
        const tabsEl = tabsSectionRef.current;
        const outerEl = outerContainerRef.current;
        if (!wrapperEl || !headerEl || !tabsEl || !outerEl) return;

        
        tabsEl.style.position = '';
        tabsEl.style.top = '';
        tabsEl.style.left = '';
        tabsEl.style.width = '';
        wrapperEl.style.position = '';
        wrapperEl.style.height = '';
        wrapperEl.style.alignContent = '';
        outerEl.style.alignItems = '';

        // Mobile always stacks in a column (see container `sx`).
        if (isMobileView) {
            outerEl.style.alignItems = 'stretch';
            return;
        }

        const headerRect = headerEl.getBoundingClientRect();
        const tabsRect = tabsEl.getBoundingClientRect();
        const hasWrapped = (tabsRect.top - headerRect.top) > WRAP_THRESHOLD_PX;

        
        
        outerEl.style.alignItems = hasWrapped ? 'flex-start' : 'center';

        
        if (!hasWrapped) return;

        const wrapperRect = wrapperEl.getBoundingClientRect();
        const outerRect = outerEl.getBoundingClientRect();
        const topPx = Math.round(tabsRect.top - wrapperRect.top);
        const widthPx = Math.round(outerRect.right - wrapperRect.left);
        const wrapperHeightPx = Math.round(wrapperRect.height);

        wrapperEl.style.position = 'relative';
        wrapperEl.style.height = `${wrapperHeightPx}px`;
        wrapperEl.style.alignContent = 'flex-start';
        tabsEl.style.position = 'absolute';
        tabsEl.style.top = `${topPx}px`;
        tabsEl.style.left = '0px';
        tabsEl.style.width = `${widthPx}px`;
    }, [isMobileView]);

    useLayoutEffect(() => {
        recalcTabsWidth();
        
    }, [recalcTabsWidth, categories, categoryCount, faceplatesCount, intl.locale, isLoading]);

    useEffect(() => {
        const outerEl = outerContainerRef.current;
        if (!outerEl || typeof ResizeObserver === 'undefined') return undefined;
        
        
        let rafId;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(recalcTabsWidth);
        });
        observer.observe(outerEl);
        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [recalcTabsWidth]);

    return (
        <>
            {(hasCategories) && (
                <Box>
                    {}
                    <Grid
                        container
                        ref={outerContainerRef}
                        className={`choose-a-design-container gif-category-tabs-row ${
                            !(faceplatesCount > 1 || categoryCount > 1)
                                ? 'single-faceplate-choose-a-design-container' : ''}`}
                    >
                        {}
                        <Box
                            className='faceplate-heading-tabs-wrapper'
                            ref={wrapperRef}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '24px',
                                flex: '1 1 auto',
                                minWidth: 0
                            }}
                        >
                            <Box className='faceplate-header-section' ref={headerSectionRef} sx={{ flexShrink: 0 }}>
                                <Grid item className='faceplate-header-wrapper'>
                                    {(faceplatesCount > 1 || categoryCount > 1) && (
                                        <Box
                                            className='faceplate-heading-row'
                                            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
                                        >
                                            <SkeletonLoader
                                                isLoading={isLoading}
                                                skeletonProps={{
                                                    animation: 'wave',
                                                    variant: 'text',
                                                    className: 'skeletonclass',
                                                    height: 85
                                                }}
                                            >
                                                <Typography
                                                    id='design-heading'
                                                    variant="h3"
                                                    className='faceplateHeaderText'
                                                    lang={intl.locale}
                                                >
                                                    {intl.formatMessage(homePageV2Messages.CarouselheadingTitle)}
                                                </Typography>
                                            </SkeletonLoader>
                                        </Box>
                                    )}
                                    {
                                        isPhotoSupported && isNewLandingPage
                                            && (faceplatesCount > 1 || categoryCount > 1) && (
                                            <UYOButton
                                                showLabel={!isMobileView}
                                                onClick={() => {
                                                    saveSelectedCardType(
                                                        isPhysicalCard ? CARD_TYPES.Physical : CARD_TYPES.Digital
                                                    );
                                                    if (!uyoFaceplate) {
                                                        saveSelectedCardOnBuyButtonClickAction(defaultCategory,
                                                            defaultFaceplate,
                                                            'UYO',
                                                            isPhysicalCard);
                                                    }
                                                    setClickedOnUYOAction(true); 
                                                    handleSubmit(isPhysicalCard, true);
                                                    trackAddAnotherButton(
                                                        'Upload Your Own Photo', HOME_STEP, 'Homepage'
                                                    );
                                                }}
                                            />
                                        )

                                    }
                                </Grid>
                            </Box>

                            {}
                            <Box
                                className='category-tabs-section'
                                ref={tabsSectionRef}
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0
                                }}
                            >
                                <Grid item xs className='category-warpper'>
                                    {}
                                    {categoryCount > 1 && (
                                        <Box
                                            sx={{ display: { xs: 'none', md: 'block' } }}
                                            onClick={handleScrollButtonClick}
                                        >
                                            <SkeletonLoader
                                                isLoading={isLoading}
                                                skeletonProps={{
                                                    animation: 'wave',
                                                    variant: 'text',
                                                    className: 'skeletonclass',
                                                    height: 85
                                                }}
                                            >
                                                <Tabs
                                                    value={selectedCategoryTabIndex}
                                                    onChange={handleTabChange}
                                                    aria-label='Design categories'
                                                    className='category-tabs-wrapper'
                                                    variant="scrollable"
                                                    scrollButtons="auto"
                                                    ScrollButtonComponent={AccessibleTabScrollButton}
                                                >
                                                    {categories.map((category, index) => {
                                                        const tabId = `tab-${index}`;
                                                        const panelId = `tabpanel-${index}`;
                                                        const isSelected = selectedCategoryTabIndex === index;
                                                        return (
                                                            <Tab
                                                                id={tabId}
                                                                aria-controls={panelId}
                                                                aria-selected={isSelected}
                                                                className='category-tabs'
                                                                label={category}
                                                                value={index}
                                                                key={tabId}
                                                                onClick={refreshCarousel}
                                                            />
                                                        );
                                                    })}
                                                </Tabs>
                                            </SkeletonLoader>
                                        </Box>
                                    )}
                                    {}
                                    {categoryCount > 1 && (
                                        <>
                                            <Box
                                                className='CarousalCategoryPicker-mobile'
                                                sx={{ display: { xs: 'block', md: 'none' } }}
                                            >
                                                <SkeletonLoader
                                                    isLoading={isLoading}
                                                    skeletonProps={{
                                                        animation: 'wave',
                                                        variant: 'text',
                                                        className: 'skeletonclass',
                                                        height: 85,
                                                        width: 100
                                                    }}
                                                >
                                                    <CarousalCategoryPicker
                                                        selectedFaceplateCategory={selectedCategory}
                                                        allCategoryList={categories}
                                                        onCategorySelect={setSelectedCategory}
                                                        refreshCarousel={refreshCarousel}
                                                        callbackOnCategorySelectOrClose={
                                                            callbackOnCategorySelectOrClose
                                                        }
                                                        stepName={HOME_STEP}
                                                        cardType={cardType}
                                                    />
                                                </SkeletonLoader>
                                            </Box>
                                        </>
                                    )}
                                </Grid>
                            </Box>
                        </Box>

                        {}
                        {!isLoading && giftCardDeliveryType !== PHYSICAL_CARD_TYPE
                            && (faceplatesCount > 1 || categoryCount > 1) && (
                            <Box sx={{ flexShrink: 0 }}>
                                <PlayAllAnimationsButton
                                    onClick={() => trackAddAnotherButton(
                                        'Play all animations', HOME_STEP, 'Homepage'
                                    )}
                                />
                            </Box>
                        )}
                    </Grid>
                    {categories.map((category, index) => {
                        const tabId = `tab-${index}`;
                        const panelId = `tabpanel-${index}`;
                        const isSelected = selectedCategoryTabIndex === index;
                        return (
                            <>
                                {isSelected && (
                                    <Grid
                                        container
                                        key={panelId}
                                        role="tabpanel"
                                        id={panelId}
                                        aria-labelledby={tabId}
                                        sx={{ display: isSelected ? 'block' : 'none' }}
                                    >
                                        <Grid item xs={12} className='carousal-body-wrapper'>
                                            { isNewLandingPage
                                                ? (
                                                    <FaceplateList
                                                        category={selectedCategory}
                                                        handleSubmit={handleSubmit}
                                                        categoryCount={categoryCount}
                                                        carouselKey={carouselKey}
                                                        giftCardDeliveryType={giftCardDeliveryType}
                                                        setFaceplatesCount={setFaceplatesCount}
                                                        setCarouselLoaded={setCarouselLoaded}
                                                        carouselLoaded={carouselLoaded}
                                                        intl={intl}
                                                        tempCurrency={tempCurrency}
                                                        shouldSetCurrency={shouldSetCurrency}
                                                        cardType={cardType}
                                                        renderCatalogAndDenom={renderCatalogAndDenom}
                                                        isPhotoSupported={isPhotoSupported}
                                                        isPhysicalCard={isPhysicalCard}
                                                        uyoFaceplate={uyoFaceplate}
                                                        isMobileView={isMobileView}
                                                        hidePicker={hidePicker}
                                                        checkoutSubHeaderText={checkoutSubHeaderText}
                                                    />
                                                )
                                                : (
                                                    <FaceplateCarousel
                                                        category={selectedCategory}
                                                        handleSubmit={handleSubmit}
                                                        categoryCount={categoryCount}
                                                        carouselKey={carouselKey}
                                                        giftCardDeliveryType={giftCardDeliveryType}
                                                        setFaceplatesCount={setFaceplatesCount}
                                                        setCarouselLoaded={setCarouselLoaded}
                                                        carouselLoaded={carouselLoaded}
                                                        intl={intl}
                                                        tempCurrency={tempCurrency}
                                                        shouldSetCurrency={shouldSetCurrency}
                                                    />
                                                )
                                            }
                                        </Grid>
                                    </Grid>
                                )}
                            </>
                        );
                    })}
                </Box>
            )}
        </>
    );
}

CategoryTabsCarouselContainer.defaultProps = {
    callbackOnCategorySelectOrClose: () => { },
    cardType: '',
    renderCatalogAndDenom: () => null,
    isNewLandingPage: false,
    hidePicker: false,
    checkoutSubHeaderText: ''
};
CategoryTabsCarouselContainer.propTypes = {
    categories: PropTypes.instanceOf(IList).isRequired,
    handleSubmit: PropTypes.func.isRequired,
    giftCardDeliveryType: PropTypes.string.isRequired,
    categoryCount: PropTypes.number.isRequired,
    faceplatesCount: PropTypes.number.isRequired,
    setFaceplatesCount: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    isLoading: PropTypes.bool.isRequired,
    callbackOnCategorySelectOrClose: PropTypes.func,
    tempCurrency: PropTypes.string.isRequired,
    shouldSetCurrency: PropTypes.bool.isRequired,
    cardType: PropTypes.string,
    renderCatalogAndDenom: PropTypes.func,
    saveSelectedCardOnBuyButtonClickAction: PropTypes.func.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    setClickedOnUYOAction: PropTypes.func.isRequired,
    trackAddAnotherButton: PropTypes.func.isRequired,
    isPhotoSupported: PropTypes.bool.isRequired,
    isPhysicalCard: PropTypes.bool.isRequired,
    uyoFaceplate: PropTypes.string.isRequired,
    defaultCategory: PropTypes.string.isRequired,
    defaultFaceplate: PropTypes.string.isRequired,
    isNewLandingPage: PropTypes.bool,
    hidePicker: PropTypes.bool,
    checkoutSubHeaderText: PropTypes.string
};

const mapDispatchToProps = {
    saveSelectedCardOnBuyButtonClickAction: saveSelectedCardOnBuyButtonClick,
    saveSelectedCardType: selectedCardType,
    setClickedOnUYOAction: setClickedOnUYO,
    trackAddAnotherButton: trackButtonClick
};

export default connect(null, mapDispatchToProps)(injectIntl(windowSize(CategoryTabsCarouselContainer)));
